import { StyleProps } from '@chakra-ui/react';
import { useMemo } from 'react';

import { ResponsiveStyleValue, StyleValue } from '../types/style';
import { useColorModeStyle } from './use-color-mode-style';

interface StyleFunctionParams<P> {
	colorMode: 'light' | 'dark';
	mode: (lightValue: ResponsiveStyleValue, darkValue: ResponsiveStyleValue) => ResponsiveStyleValue;
	props: P;
	value: StyleValue;
}

type VariantPropValue = string | boolean;

type StyleFunction<P> = ({ colorMode, mode, props, value }: StyleFunctionParams<P>) => StyleProps;

type Style<P> = StyleProps | StyleFunction<P>;

type Variants<P> = Record<string, Style<P>>;

type StyleVariant<P> = Style<P> | Variants<P>;

export type VariantStyles<P> = Record<string, StyleVariant<P>>;

export interface VariantStylesConfig<P> {
	defaults?: Partial<Record<keyof P, VariantPropValue>>;
}

export const createUseVariantStyle = <P extends Record<string, any>, T extends VariantStyles<P> = VariantStyles<P>>(
	styles: T,
	config: VariantStylesConfig<T> = {}
) => {
	const useVariantStyle = (props: P) => {
		const { colorMode, mode } = useColorModeStyle();

		return useMemo(() => {
			const propsWithDefaults = {
				...config.defaults,
				...props,
			} as Record<string, string | boolean>;

			return Object.entries(styles).reduce<StyleProps>((styleObject, [property, fnStylesOrVariants]) => {
				const selectedVariant = propsWithDefaults[property];

				if (!selectedVariant) {
					return styleObject;
				}

				if (typeof fnStylesOrVariants === 'function') {
					return {
						...styleObject,
						...fnStylesOrVariants({
							value: selectedVariant,
							props,
							mode,
							colorMode,
						}),
					};
				}

				// Return all styles for boolean variant
				if (selectedVariant === true) {
					return {
						...styleObject,
						...(fnStylesOrVariants as StyleProps),
					};
				}

				const fnOrStyles = (fnStylesOrVariants as Variants<P>)[selectedVariant];

				if (typeof fnOrStyles === 'function') {
					return {
						...styleObject,
						...fnOrStyles({ value: selectedVariant, props, mode, colorMode }),
					};
				}

				return {
					...styleObject,
					...fnOrStyles,
				};
			}, {});
			// Ignoring React Hooks lint because the nested nature of this hook allows it to miss
			// the necessary inclusion of `config` and `styles` passed from the parent.
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [colorMode, config, mode, props, styles]);
	};

	return useVariantStyle;
};
